import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Calendar, dateFnsLocalizer, View } from "react-big-calendar";
import { format, parse, startOfWeek, getDay, differenceInMinutes } from "date-fns";
import enUS from "date-fns/locale/en-US";
import { IonButton, IonIcon } from "@ionic/react";
import { banOutline, chevronBackOutline, chevronForwardOutline } from "ionicons/icons";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { CalendarJobEvent, getEventStyle } from "../utils/calendarUtils";

// ─── date-fns localizer ───────────────────────────────────────────────────────

const locales = { "en-US": enUS };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 0 }),
  getDay,
  locales,
});

// ─── Props ────────────────────────────────────────────────────────────────────

interface CalendarViewMobileProps {
  events: CalendarJobEvent[];
  onSelectJob: (resource: CalendarJobEvent["resource"]) => void;
  isScheduler: boolean;
  onLabelChange?: (label: string) => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatDuration(start: Date, end: Date): string {
  const mins = differenceInMinutes(end, start);
  if (mins <= 0) return "";
  const hrs = mins / 60;
  return hrs % 1 === 0 ? `${hrs}h` : `${hrs.toFixed(1)}h`;
}

// ─── Toolbar ─────────────────────────────────────────────────────────────────

const MobileToolbar: React.FC<{
  label: string;
  onNavigate: (action: "PREV" | "NEXT" | "TODAY") => void;
  isScheduler: boolean;
  onLabelChange?: (label: string) => void;
}> = ({ label, onNavigate, isScheduler, onLabelChange }) => {
  const roleColor = isScheduler ? "secondwarning" : "warning";

  useEffect(() => {
    onLabelChange?.(label);
  }, [label, onLabelChange]);

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "4px 8px",
      borderBottom: "1px solid var(--ion-color-light-shade)",
      flexShrink: 0,
    }}>
      <IonButton fill="clear" size="small" color={roleColor} onClick={() => onNavigate("PREV")} style={{ margin: 0 }}>
        <IonIcon icon={chevronBackOutline} slot="icon-only" />
      </IonButton>

      <strong style={{ fontSize: 15 }}>{label}</strong>

      <IonButton fill="clear" size="small" color={roleColor} onClick={() => onNavigate("NEXT")} style={{ margin: 0 }}>
        <IonIcon icon={chevronForwardOutline} slot="icon-only" />
      </IonButton>
    </div>
  );
};



// ─── Main component ───────────────────────────────────────────────────────────

const CalendarViewMobile: React.FC<CalendarViewMobileProps> = ({
  events,
  onSelectJob,
  isScheduler,
  onLabelChange,
}) => {
  const today = useMemo(() => new Date(), []);
  const [currentDate, setCurrentDate] = useState(today);
  const [selectedDay, setSelectedDay] = useState<Date>(today);

  const roleColor = isScheduler ? "secondwarning" : "warning";
  const roleCssVar = isScheduler ? "var(--ion-color-secondwarning)" : "var(--ion-color-warning)";

  // Build a map: dateString → count of events for badge rendering
  const eventCountByDay = useMemo(() => {
    const map: Record<string, number> = {};
    for (const ev of events) {
      const key = ev.start.toDateString();
      map[key] = (map[key] ?? 0) + 1;
    }
    return map;
  }, [events]);

  // Jobs for the selected day, sorted by start time
  const selectedDayJobs = useMemo(
    () =>
      events
        .filter((ev) => isSameDay(ev.start, selectedDay))
        .sort((a, b) => a.start.getTime() - b.start.getTime()),
    [events, selectedDay]
  );

  const handleNavigate = useCallback((action: "PREV" | "NEXT" | "TODAY") => {
    setCurrentDate((prev) => {
      if (action === "TODAY") return new Date();
      const d = new Date(prev);
      d.setMonth(d.getMonth() + (action === "NEXT" ? 1 : -1));
      return d;
    });
  }, []);

  // Custom toolbar
  const CustomToolbar = useCallback(
    (tbProps: any) => (
      <MobileToolbar
        label={tbProps.label}
        onNavigate={(action) => { handleNavigate(action); tbProps.onNavigate(action); }}
        isScheduler={isScheduler}
        onLabelChange={onLabelChange}
      />
    ),
    [handleNavigate, isScheduler, onLabelChange]
  );

  // Suppress event pills — return nothing
  const renderEvent = useCallback(() => null, []);

  // Custom date cell header: number + badge
  const CustomDateHeader = useCallback(
    ({ date, label }: { date: Date; label: string }) => {
      const count = eventCountByDay[date.toDateString()] ?? 0;
      const isToday = isSameDay(date, today);
      const isSelected = isSameDay(date, selectedDay);

      return (
        <div
          style={{ textAlign: "center", padding: "2px 0", cursor: "pointer", position: "relative" }}
          onClick={() => setSelectedDay(date)}
        >
          <div style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 26,
            height: 26,
            borderRadius: "50%",
            backgroundColor: isSelected ? "var(--ion-color-medium)" : isToday ? "rgba(128,128,128,0.25)" : "transparent",
            fontWeight: isToday || isSelected ? 700 : 400,
            fontSize: 13,
            color: isSelected ? "#fff" : undefined,
          }}>
            {label}
          </div>
          {count > 0 && (
            <div style={{
              position: "absolute",
              top: 0,
              right: "10%",
              minWidth: 14,
              height: 14,
              borderRadius: "50%",
              backgroundColor: "var(--ion-color-danger)",
              color: "#fff",
              fontSize: 9,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
              padding: "0 2px",
            }}>
              {count}
            </div>
          )}
        </div>
      );
    },
    [eventCountByDay, selectedDay, today, roleCssVar]
  );

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>

      {/* ── Compact month calendar ─────────────────────────────────────── */}
      <div className="calendar-mobile" style={{ flexShrink: 0, height: 240 }}>
        <Calendar
          localizer={localizer}
          events={events}
          date={currentDate}
          view={"month" as View}
          views={["month"]}
          onView={() => {}}
          onNavigate={setCurrentDate}
          onSelectSlot={({ start }) => setSelectedDay(start as Date)}
          selectable
          components={{
            toolbar: CustomToolbar,
            event: renderEvent,
            month: { dateHeader: CustomDateHeader },
          }}
          style={{ height: "100%" }}
        />
      </div>

      {/* ── Job list ───────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: "auto", borderTop: "1px solid var(--ion-color-light-shade)" }}>
        {selectedDayJobs.length === 0 ? (
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            color: "var(--ion-color-medium)",
            fontSize: 14,
          }}>
            No jobs today
          </div>
        ) : (
          selectedDayJobs.map((ev) => {
            const evStyle = getEventStyle(ev);
            const bg = evStyle.style.backgroundColor as string;
            const opacity = evStyle.style.opacity as number | undefined;
            const duration = formatDuration(ev.start, ev.end);

            return (
              <div
                key={`${ev.resource.firestoreDocId}-${ev.start.getTime()}`}
                onClick={() => onSelectJob(ev.resource)}
                style={{
                  margin: "8px 12px",
                  borderRadius: 7,
                  background: "var(--ion-card-background, var(--ion-background-color))",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                  overflow: "hidden",
                  cursor: "pointer",
                  opacity: opacity ?? 1,
                  textDecoration: evStyle.style.textDecoration as string | undefined,
                }}
              >
                <div style={{ display: "flex" }}>
                  {/* Card body */}
                  <div style={{ flex: 1, padding: "10px 12px" }}>
                    {/* Title row */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div style={{ fontWeight: 800, fontSize: 15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>
                        {ev.resource.company || ev.resource.shiftIdentifier}
                      </div>
                      {ev.resource.isCancelled && (
                        <IonIcon icon={banOutline} color="danger" style={{ fontSize: 18, flexShrink: 0, marginLeft: 8 }} />
                      )}
                    </div>

                    {/* Location */}
                    {ev.resource.location ? (
                      <div style={{ fontSize: 12, color: "var(--ion-color-medium)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {ev.resource.location}
                      </div>
                    ) : null}

                    {/* Time + Pay */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                      <div style={{ fontSize: 13 }}>
                        <div>
                          <span style={{ fontSize: 10, opacity: 0.5, textTransform: "uppercase", letterSpacing: "0.03em", marginRight: 4 }}>Start</span>
                          {format(ev.start, "h:mm a")}
                        </div>
                        <div>
                          <span style={{ fontSize: 10, opacity: 0.5, textTransform: "uppercase", letterSpacing: "0.03em", marginRight: 4 }}>End</span>
                          {format(ev.end, "h:mm a")}{duration ? ` · ${duration}` : ""}
                        </div>
                      </div>
                      {ev.resource.pay ? (
                        <div style={{ fontWeight: 900, fontSize: 13, flexShrink: 0, paddingLeft: 8 }}>
                          {ev.resource.pay}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

    </div>
  );
};

export default CalendarViewMobile;
